import { TailwindWrapper } from "@utils/TailwindWrapper";
import { Text } from "@ui-component-library";
import MediaSelector from "../editor/fields/MediaSelector";
import LayoutSelector from "../editor/fields/LayoutSelector";
import React from "react";

const FeatureCards = (props) => {
  let layout = {
    firstSpan: "",
    secondSpan: "",
    thirdSpan: "",
  }

  let defaultSrc = {
    first: "",
    second: "",
    third: "",
  }

  switch (props.layout) {
    case 'oneThirdTwoThirds':
      layout = {
        firstSpan: "lg:col-span-1",
        secondSpan: "lg:col-span-2",
        thirdSpan: "",
      }
      defaultSrc = {
        first: "https://placehold.co/400x340",
        second: "https://placehold.co/820x340",
        third: "",
      }
      break;

    case 'twoThirdsOneThird':
      layout = {
        firstSpan: "lg:col-span-2",
        secondSpan: "lg:col-span-1",
        thirdSpan: "",
      }
      defaultSrc = {
        first: "https://placehold.co/820x340",
        second: "https://placehold.co/400x340",
        third: "",
      }
      break;

    case 'threeEqual':
      layout = {
        firstSpan: "lg:col-span-1",
        secondSpan: "lg:col-span-1",
        thirdSpan: "lg:col-span-1",
      }
      defaultSrc = {
        first: "https://placehold.co/400x340",
        second: "https://placehold.co/400x340",
        third: "https://placehold.co/400x340",
      }
      break;
    
    case 'onlyOne':
      layout = {
        firstSpan: "lg:col-span-3",
        secondSpan: "",
        thirdSpan: "",
      }
      defaultSrc = {
        first: "https://placehold.co/1220x340",
        second: "",
        third: "",
      }
      break;
  
    default:
      layout = {
        firstSpan: "lg:col-span-1",
        secondSpan: "lg:col-span-2",
        thirdSpan: "",
      }
      defaultSrc = {
        first: "https://placehold.co/400x340",
        second: "https://placehold.co/820x340",
        third: "",
      }
      break;
  }

  const firstCardProps = {
    src: (props.firstCardSrc ?? props.LeftCardSrc) || defaultSrc.first,
    alt: props.firstCardAlt ?? props.LeftCardAlt,
    title: props.firstCardTitle ?? props.LeftCardTitle,
    text: props.firstCardText ?? props.LeftCardText,
    url: props?.firstCardLink ?? props.LeftCardLink,
    widthClass: layout.firstSpan,
  };

  const secondCardProps = {
    src: (props.secondCardSrc ?? props.RightCardSrc) || defaultSrc.second,
    alt: props.secondCardAlt ?? props.RightCardAlt,
    title: props.secondCardTitle ?? props.RightCardTitle,
    text: props.secondCardText ?? props.RightCardText,
    url: props?.secondCardLink ?? props.RightCardLink,
    widthClass: layout.secondSpan,
  };
  
  const thirdCardProps = {
    src: props.thirdCardSrc || defaultSrc.third,
    alt: props.thirdCardAlt,
    title: props.thirdCardTitle,
    text: props.thirdCardText,
    url: props?.thirdCardLink,
    widthClass: layout.thirdSpan,
  };

  const Card = ({ src, alt, title, text, url, widthClass = "" }) => {
    const Container = url ? "a" : "div";
    return (
      <Container
        className={`flex h-full w-full flex-col overflow-hidden rounded-xl ${widthClass} ${
          url ? "cursor-pointer" : ""
        }`}
        href={url ? url : undefined}
      >
        <div className="relative bg-gray-100">
          <img
            alt={alt}
            src={src}
            className="h-[366px] w-full object-cover object-center"
          />
        </div>
        <div className="flex flex-1 flex-col gap-2 border-t border-gray-200 bg-[#f7f7f7] p-6">
          <Text tw variant="text-lg/leading-normal/medium">
            {title}
          </Text>
          <Text
            tw
            variant="text-base/leading-normal/normal"
            className="mt-2 text-[#737373]"
          >
            {text}
          </Text>
        </div>
      </Container>
    );
  };

  return (
    <TailwindWrapper>
      <section className={`w-full py-3 bg-white container ${props.bottomPadding ? 'pb-12 lg:pb-24' : ''}`}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch lg:auto-rows-fr">
          <Card {...firstCardProps} />
          {props.layout !== 'onlyOne' && <Card {...secondCardProps} />}
          {props.layout === 'threeEqual' && <Card {...thirdCardProps} />}
        </div>
      </section>
    </TailwindWrapper>
  );
};

FeatureCards.defaultProps = {
  bottomPadding: false,
  layout: "oneThirdTwoThirds",
  firstCardSrc: "",
  firstCardAlt: "Feature image",
  firstCardTitle: "Feature title 1",
  firstCardText:
    "Shortly describe how this feature solves a specific user problem. Focus on benefits rather than features.",
  firstCardLink: undefined,
  secondCardSrc: "",
  secondCardAlt: "Feature image",
  secondCardTitle: "Feature title 2",
  secondCardText:
    "Shortly describe how this feature solves a specific user problem. Focus on benefits rather than features.",
  secondCardLink: undefined,
  thirdCardSrc: "",
  thirdCardAlt: "Feature image",
  thirdCardTitle: "Feature title 3",
  thirdCardText:
    "Shortly describe how this feature solves a specific user problem. Focus on benefits rather than features.",
  thirdCardLink: undefined,
};

// Migrations for old keys to new keys, the resolveData replaces the old keys with the new ones in the fields
const OLD_TO_NEW_KEYS = [
  ['firstCardSrc', 'LeftCardSrc'],
  ['firstCardAlt', 'LeftCardAlt'],
  ['firstCardTitle', 'LeftCardTitle'],
  ['firstCardText', 'LeftCardText'],
  ['firstCardLink', 'LeftCardLink'],
  ['secondCardSrc', 'RightCardSrc'],
  ['secondCardAlt', 'RightCardAlt'],
  ['secondCardTitle', 'RightCardTitle'],
  ['secondCardText', 'RightCardText'],
  ['secondCardLink', 'RightCardLink'],
];

FeatureCards.resolveData = ({ props }) => {
  const migrations = Object.fromEntries(
    OLD_TO_NEW_KEYS.filter(
      ([newKey, oldKey]) => props[newKey] == null && props[oldKey] != null
    ).map(([newKey, oldKey]) => [newKey, props[oldKey]])
  );
  return { props: { ...props, ...migrations } };
};

const LAYOUT_FIELD = {
  bottomPadding: {
    type: "select",
    options: [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ],
  },
  layout: {
    type: "custom",
    label: "Layout",
    render: ({ onChange, value }) => {
      return <LayoutSelector onChange={onChange} value={value} />;
    },
  },
};

const FIRST_CARD_FIELDS = {
  firstCardSrc: {
    type: "custom",
    render: ({ name, onChange, value }) => (
      <MediaSelector
        name={name}
        label="First Card Image Source"
        type={"img"}
        onChange={onChange}
        value={value}
      />
    ),
  },
  firstCardAlt: {
    type: "text",
    label: "First Card Image Alt Text",
  },
  firstCardTitle: {
    type: "text",
    label: "First Card Title",
  },
  firstCardText: {
    type: "textarea",
    label: "First Card Description",
  },
  firstCardLink: {
    type: "text",
    label: "First Card Link",
  },
};

const SECOND_CARD_FIELDS = {
  secondCardSrc: {
    type: "custom",
    render: ({ name, onChange, value }) => (
      <MediaSelector
        name={name}
        label="Second Card Image Source"
        type={"img"}
        onChange={onChange}
        value={value}
      />
    ),
  },
  secondCardAlt: {
    type: "text",
    label: "Second Card Image Alt Text",
  },
  secondCardTitle: {
    type: "text",
    label: "Second Card Title",
  },
  secondCardText: {
    type: "textarea",
    label: "Second Card Description",
  },
  secondCardLink: {
    type: "text",
    label: "Second Card Link",
  },
};

const THIRD_CARD_FIELDS = {
  thirdCardSrc: {
    type: "custom",
    render: ({ name, onChange, value }) => (
      <MediaSelector
        name={name}
        label="Third Card Image Source"
        type={"img"}
        onChange={onChange}
        value={value}
      />
    ),
  },
  thirdCardAlt: {
    type: "text",
    label: "Third Card Image Alt Text",
  },
  thirdCardTitle: {
    type: "text",
    label: "Third Card Title",
  },
  thirdCardText: {
    type: "textarea",
    label: "Third Card Description",
  },
  thirdCardLink: {
    type: "text",
    label: "Third Card Link",
  },
};

FeatureCards.resolveFields = (data) => {
  const layout = data.props?.layout ?? "oneThirdTwoThirds";
  const fields = {
    ...LAYOUT_FIELD,
    ...FIRST_CARD_FIELDS,
  };
  if (layout !== "onlyOne") {
    Object.assign(fields, SECOND_CARD_FIELDS);
  }
  if (layout === "threeEqual") {
    Object.assign(fields, THIRD_CARD_FIELDS);
  }
  return fields;
};

export default FeatureCards;
